export interface IRecaptchaSettings {
  isRecaptchaEnabled: boolean,
  siteKey: string,
  enabledVersion: number;
}

export enum RecaptchaVersion {
  none = 0,
  reCAPTCHAv2 = 1,
  reCAPTCHAv3 = 2,
}

export type RecaptchaAction = 'signup';

export class RecaptchaService {
constructor(private $form: HTMLFormElement, private recaptchaSettings: IRecaptchaSettings) {
}

  private getRecaptchaInput(selector: string): HTMLInputElement {
    const el = this.$form.querySelector<HTMLInputElement>(selector);
    if (!el) {
      throw new Error('Recaptcha input not found');
    }
    return el;
  }

  private v2WidgetId: number | null = null;

  /**
   * Legacy: render an invisible v2 widget so we can getResponse(widgetId).
   * We keep this, but prefer the visible checkbox (auto-render) when present.
   */
  private ensureV2Rendered(siteKey: string): void {
    if (this.v2WidgetId !== null) return;

    if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
      throw new Error('Recaptcha v2 script not loaded');
    }

    let container = document.getElementById('recaptcha-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'recaptcha-container';
      container.style.display = 'none';
      document.body.appendChild(container);
    }

    this.v2WidgetId = window.grecaptcha.render(container, { sitekey: siteKey });
  }

  async challenge(action: RecaptchaAction): Promise<string | undefined> {
    const { enabledVersion: version, siteKey } = this.recaptchaSettings;

    if (version === RecaptchaVersion.reCAPTCHAv2) {
      if (!window.grecaptcha || typeof window.grecaptcha.getResponse !== 'function') {
        throw new Error('Recaptcha v2 script not loaded');
      }

      let token = window.grecaptcha.getResponse();

      if (!token) {
        if (!siteKey) throw new Error('Recaptcha site key not found');
        this.ensureV2Rendered(siteKey);
        token = window.grecaptcha.getResponse(this.v2WidgetId!);
      }

      return token;
    }

    if (version === RecaptchaVersion.reCAPTCHAv3) {
      const responseInput = this.getRecaptchaInput('input[name="g-recaptcha-response"]');

      if (!siteKey) throw new Error('Recaptcha site key not found');
      if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') {
        throw new Error('Recaptcha v3 script not loaded');
      }

      await new Promise<void>((resolve) => {
        window.grecaptcha!.ready(() => resolve());
      });

      const token = await window.grecaptcha.execute(siteKey, { action });

      if (!token) throw new Error('Recaptcha v3 token error');

      responseInput.value = token;
      return token;
    }

    return undefined;
  }

  reset(): void {
    const { enabledVersion: version } = this.recaptchaSettings;

    const responseInput = this.$form.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
    if (responseInput) responseInput.value = '';

    if (version === RecaptchaVersion.reCAPTCHAv2 && window.grecaptcha?.reset) {
      try { window.grecaptcha.reset(); } catch (_) { /* ignore */ }
      if (this.v2WidgetId !== null) {
        try { window.grecaptcha.reset(this.v2WidgetId); } catch (_) { /* ignore */ }
      }
    }
  }
}
